import { useEffect, useMemo, useState } from 'react';
import { ActiveEventTimesProps } from '@v2/pages/batch/details/modules/batch/components/ProductivityStatusInfo/components/ActiveEventTimes';
import { useSetInterval } from '@v2/pages/common/hooks/useSetInterval';
import moment from 'moment';
import { machineStatus } from '@helpers';
import { TimeBlockItemProps } from '@v2/pages/batch/details/modules/batch/components/ProductivityStatusInfo/components/TimeBlockItem';
import { TimeCategory } from '@interfaces/base';
import { useFilterTimeCategoriesData } from '@v2/pages/common/hooks/useFilterTimeCategoriesData';

interface UseManageActiveEventTimesParams
  extends Pick<ActiveEventTimesProps, 'offset' | 'actualTimeForLatestEvent' | 'increaseActualTime' | 'batch' | 'eventDetails' | 'setAmountOfLines' | 'allowedTimeCategories'> {}

export const useManageActiveEventTimes = ({
  offset = 0,
  actualTimeForLatestEvent,
  batch,
  increaseActualTime,
  eventDetails,
  setAmountOfLines,
  allowedTimeCategories,
}: UseManageActiveEventTimesParams) => {
  const offsetInSeconds = Math.floor(offset / 1000);

  const [actualTime, setActualTime] = useState<number>((actualTimeForLatestEvent ?? 0) + offsetInSeconds);

  useSetInterval({
    disabled: !increaseActualTime || !batch?.isInProgress || actualTime >= 60 * 60 * 100,
    action: () => setActualTime((prev) => prev + 1),
    dependencyArray: [batch?.isInProgress],
  });

  useEffect(() => {
    if (actualTimeForLatestEvent != null) {
      setActualTime(actualTimeForLatestEvent);
    } else {
      if (eventDetails) {
        const actualTime = moment(eventDetails?.endedOn).diff(eventDetails?.startedOn, 'seconds');

        setActualTime(actualTime);
      }
    }
  }, [eventDetails, actualTimeForLatestEvent]);

  const timeValues = useMemo(() => {
    if (eventDetails) {
      switch (eventDetails.status) {
        case machineStatus.ACTIVE: {
          return {
            scheduled: batch?.scheduledActiveTimeAverageInSeconds ?? 0,
            ideal: batch?.idealActiveTimeAverageInSeconds ?? 0,
            paid: batch?.paidActiveTimeAverageInSeconds ?? 0,
          };
        }
        case machineStatus.CHANGE: {
          return {
            scheduled: batch?.scheduledChangeTimeAverageInSeconds ?? 0,
            ideal: batch?.idealChangeTimeAverageInSeconds ?? 0,
            paid: batch?.paidChangeTimeAverageInSeconds ?? 0,
          };
        }
        case machineStatus.SETUP: {
          return {
            scheduled: batch?.scheduledSetupTimeInSeconds ?? 0,
            ideal: batch?.idealSetupTimeInSeconds ?? 0,
            paid: batch?.paidSetupTimeInSeconds ?? 0,
          };
        }
        default:
          return {
            scheduled: 0,
            ideal: 0,
            paid: 0,
          };
      }
    }
  }, [eventDetails]);

  const productiveTimeBlock: TimeBlockItemProps['timeBlockItem'][] = [
    {
      name: TimeCategory.ACTUAL,
      key: 'batchActual',
      icon: 'actual',
      color: 'Status/Light/Productive',
      timeValue: actualTime ?? 0,
      width: 15,
      height: 15,
    },
    {
      key: 'batchScheduled',
      name: TimeCategory.SCHEDULED,
      icon: 'scheduledTime',
      color: 'Selected/Light/Primary',
      timeValue: timeValues?.scheduled ?? 0,
      width: 16,
      height: 16,
    },
    {
      name: TimeCategory.IDEAL,
      key: 'batchIdeal',
      icon: 'runningBunny',
      color: 'Label/Light/Tertiary',
      timeValue: timeValues?.ideal ?? 0,
      width: 16,
      height: 20,
    },
    {
      name: TimeCategory.PAID,
      key: 'batchPaid',
      icon: 'euro',
      color: 'Status/Light/Paid',
      timeValue: timeValues?.paid ?? 0,
      width: 11,
      height: 20,
    },
  ];

  const filteredProductivityStatusBlock = useFilterTimeCategoriesData<TimeBlockItemProps['timeBlockItem']>({ batch, data: productiveTimeBlock, allowedTimeCategories });

  useEffect(() => {
    if (!setAmountOfLines) return;
    setAmountOfLines(filteredProductivityStatusBlock.length);
  }, [filteredProductivityStatusBlock.length, !!setAmountOfLines]);

  return { filteredProductivityStatusBlock };
};
